import React, { useState, useContext, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useLocation } from 'react-router-dom';
import { createUserWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import { createUserProfile, getUserProfile } from '../user/user.service';
import { getJobDescriptionData } from '../onboarding/onboarding.service';
import { SignUpFormData } from './forms.types';
import SignUpUI from './ui/signup.ui';
import { RootStoreContext } from '../core/root-store';
import { OnboardingStep } from '../onboarding/onboarding.type';
import { getFirebaseSignupErrorMessage } from './helpers';
import { UserRole } from '../user/user.type';

const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // At least 8 characters
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }

  // At least one capital letter
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  // At least one number OR special character
  if (!/[0-9]/.test(password) && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one number or special character');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

const SignUp = observer(() => {
  const location = useLocation();
  const state = location.state as { userRole?: UserRole } | null;

  const determineRoleFromRoute = () => {
    if (state?.userRole) {
      return state.userRole;
    }

    return undefined;
  };

  const initialRole = determineRoleFromRoute();

  const [formData, setFormData] = useState<SignUpFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    isProfileCompleted: false,
    hasVerifiedPhoneNumber: false,
    hasCompletedProfileInfo: false,
    hasCompletedLookingFor: false,
    hasCompletedProfessionalDescriptionPage: false,
    hasCompletedPTest: false,
    hasSubmittedDocumentsForVerification: false,
    hasAccess: false,
    hasVisitedPaywall: false,
    role: initialRole,
    webOnboarding: {
      onboardingStep: null,
    },
  });
  const [error, setError] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  const navigate = useNavigate();
  const rootStore = useContext(RootStoreContext);
  const userStore = rootStore.userStore;
  const onboardingStore = rootStore.onboardingStore;

  // Check if user is already logged in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userProfile = await getUserProfile(user.uid);
          if (userProfile) {
            // User is already logged in, redirect appropriately
            if (userProfile.webOnboarding?.onboardingStep === OnboardingStep.OnboardingCompleted) {
              navigate('/dashboard');
            } else {
              const nextStep = userProfile.webOnboarding?.onboardingStep
                ? onboardingStore.getNextStep(userProfile.webOnboarding.onboardingStep)
                : OnboardingStep.PhoneNumber;
              navigate(`/onboarding?step=${nextStep}`);
            }
          }
        } catch (error) {
          setCheckingAuth(false);
        }
      } else {
        setCheckingAuth(false);
      }
    });

    return () => unsubscribe();
  }, [navigate, onboardingStore]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });

    if (error) {
      setError('');
    }

    if (name === 'password') {
      const validation = validatePassword(value);
      setPasswordErrors(validation.errors);
    }

    if (name === 'confirmPassword' && passwordErrors.length > 0) {
      const validation = validatePassword(formData.password);
      setPasswordErrors(validation.errors);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      setPasswordErrors(passwordValidation.errors);
      setError('Please fix the password requirements above');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setPasswordErrors([]);

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password,
      );

      // Get hiring company from job role data if available
      let hiringCompany = '';
      const jobRoleId = sessionStorage.getItem('currentJobRoleId');

      if (jobRoleId) {
        try {
          const jobRoleData = await getJobDescriptionData(jobRoleId);
          hiringCompany = jobRoleData?.hiringCompany || '';
        } catch (error) {}
      }

      const profileData: any = {
        email: formData.email,
        ...(formData.role && { role: formData.role }),
        isProfileCompleted: formData.isProfileCompleted,
        hasVerifiedPhoneNumber: formData.hasVerifiedPhoneNumber,
        hasCompletedProfileInfo: formData.hasCompletedProfileInfo,
        hasCompletedLookingFor: formData.hasCompletedLookingFor,
        hasCompletedProfessionalDescriptionPage: formData.hasCompletedProfessionalDescriptionPage,
        hasCompletedPTest: formData.hasCompletedPTest,
        hasSubmittedDocumentsForVerification: formData.hasSubmittedDocumentsForVerification,
        hasAccess: formData.hasAccess,
        hasVisitedPaywall: formData.hasVisitedPaywall,
        webOnboarding: formData.role === UserRole.SoftwareEngineer
          ? {
            appliedFor: hiringCompany,
            optedIn: false,
            meetingInviteSent: false,
            onboardingStep: null,
          }
          : {
            onboardingStep: null,
          },
        ...(formData.role === UserRole.Founder && {
          hasSeenWelcomeAgreement: false,
          hasAgreedToProTerms: null,
        }),
      };

      await createUserProfile(userCredential.user.uid, profileData);

      await new Promise(resolve => setTimeout(resolve, 500));

      const userProfile = await getUserProfile(userCredential.user.uid);

      if (userProfile) {
        userStore.setReturningUser(false);
        userStore.update(userProfile);

        await new Promise(resolve => setTimeout(resolve, 100));

        navigate(`/onboarding?step=${OnboardingStep.PhoneNumber}`);
      } else {
        setError('Failed to load user profile after signup');
      }
    } catch (error: any) {
      setError(getFirebaseSignupErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // Don't render signup form while checking auth
  if (checkingAuth) {
    return null;
  }

  return (
    <SignUpUI
      formData={formData}
      error={error}
      passwordErrors={passwordErrors}
      loading={loading}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />
  );
});

export default SignUp;
